import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';

// Import step components
import WelcomeStep from './WelcomeStep';
import CompanySetupStep from './CompanySetupStep';
import ProfileSetupStep from './ProfileSetupStep';
import PreferencesStep from './PreferencesStep';
import FeatureTourStep from './FeatureTourStep';
import CompletionStep from './CompletionStep';

// Apple-inspired design system (matching Dashboard)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const OnboardingModal = ({ onClose, onComplete }) => {
  const {
    currentStep,
    totalSteps,
    onboardingState,
    nextStep,
    previousStep,
    stopOnboarding,
    getCurrentStepInfo,
    getProgress
  } = useOnboarding();

  // Store save functions from step components
  const stepSaveFunctions = React.useRef({});

  // Dynamic step components based on user type
  const getStepComponent = () => {
    const stepName = onboardingState.steps[currentStep];
    
    switch (stepName) {
      case 'welcome':
        return WelcomeStep;
      case 'companySetup':
        return CompanySetupStep;
      case 'profile':
        return ProfileSetupStep;
      case 'preferences':
        return PreferencesStep;
      case 'featureTour':
        return FeatureTourStep;
      case 'completion':
        return CompletionStep;
      default:
        return WelcomeStep;
    }
  };

  const StepComponent = getStepComponent();
  const stepInfo = getCurrentStepInfo();
  const progress = getProgress();

  const handleNext = async () => {
    if (stepInfo.isLast) {
      handleComplete();
      return;
    }

    // Check if current step has a save function (Profile and Preferences steps)
    const currentStepName = stepInfo.name;
    const saveFunction = stepSaveFunctions.current[currentStepName];
    
    if (saveFunction) {
      // Call the save function and only proceed if successful
      const success = await saveFunction();
      if (success) {
        nextStep();
      }
      // If save fails, don't advance to next step
    } else {
      // Steps without save functions (Welcome, FeatureTour, Completion)
      nextStep();
    }
  };

  const handlePrevious = () => {
    if (!stepInfo.isFirst) {
      previousStep();
    }
  };

  const handleComplete = () => {
    stopOnboarding();
    onComplete?.();
    onClose?.();
  };

  const handleSkip = () => {
    stopOnboarding();
    onClose?.();
  };

  // Prevent closing modal by clicking outside or ESC key during onboarding
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.85) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 20px -5px rgba(0, 0, 0, 0.1)',
          border: `1px solid rgba(148, 163, 184, 0.2)`,
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          fontFamily: fontStyles.fontFamily
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b transition-all duration-200 ease-out"
          style={{
            borderColor: 'rgba(148, 163, 184, 0.15)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
            backdropFilter: 'blur(12px)',
            borderRadius: '24px 24px 0 0',
          }}
        >
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                  boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)'
                }}
              >
                <span 
                  className="text-white font-semibold text-sm"
                  style={fontStyles.medium}
                >
                  S
                </span>
              </div>
              <div>
                <h2 
                  className="text-xl text-gray-900"
                  style={{
                    ...fontStyles.heading,
                    color: colors.gray900
                  }}
                >
                  Welcome to SignPact
                </h2>
                <p 
                  className="text-sm"
                  style={{
                    ...fontStyles.body,
                    color: colors.gray500
                  }}
                >
                  Let's get you set up in just a few steps
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleSkip}
            className="p-2 transition-colors rounded-lg"
            title="Skip onboarding"
            style={{
              color: colors.gray500,
            }}
            onMouseEnter={(e) => {
              e.target.style.color = colors.gray700;
              e.target.style.backgroundColor = 'rgba(148, 163, 184, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = colors.gray500;
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div 
          className="px-6 py-4 border-b"
          style={{
            borderColor: 'rgba(148, 163, 184, 0.15)',
            background: 'linear-gradient(135deg, rgba(249, 249, 249, 0.8), rgba(243, 243, 243, 0.6))',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span 
              className="text-sm font-medium"
              style={{
                ...fontStyles.medium,
                color: colors.gray700
              }}
            >
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span 
              className="text-sm"
              style={{
                ...fontStyles.body,
                color: colors.gray500
              }}
            >
              {progress}% Complete
            </span>
          </div>
          <div 
            className="w-full rounded-full h-2"
            style={{
              backgroundColor: colors.gray200
            }}
          >
            <motion.div
              className="h-2 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                boxShadow: `0 2px 8px rgba(30, 64, 175, 0.3)`
              }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              <StepComponent
                onNext={handleNext}
                onPrevious={handlePrevious}
                onSkip={handleSkip}
                onSave={(saveFunction) => {
                  stepSaveFunctions.current[stepInfo.name] = saveFunction;
                }}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div 
          className="flex items-center justify-between p-6 border-t"
          style={{
            borderColor: 'rgba(148, 163, 184, 0.15)',
            background: 'linear-gradient(135deg, rgba(249, 249, 249, 0.9), rgba(243, 243, 243, 0.7))',
            backdropFilter: 'blur(12px)',
            borderRadius: '0 0 24px 24px'
          }}
        >
          <div className="flex items-center space-x-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full transition-colors duration-300"
                style={{
                  backgroundColor: index <= currentStep ? colors.accent : colors.gray300,
                  boxShadow: index <= currentStep ? `0 0 8px ${colors.accent}40` : 'none'
                }}
              />
            ))}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleSkip}
              className="px-4 py-2 transition-colors rounded-lg"
              style={{
                color: colors.gray500,
                ...fontStyles.medium
              }}
              onMouseEnter={(e) => {
                e.target.style.color = colors.gray700;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.gray500;
              }}
            >
              Skip for now
            </button>
            
            {!stepInfo.isFirst && (
              <button
                onClick={handlePrevious}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
                  color: colors.gray700,
                  border: `1px solid rgba(148, 163, 184, 0.2)`,
                  ...fontStyles.medium,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                }}
              >
                <ArrowLeft size={16} />
                <span>Previous</span>
              </button>
            )}
            
            <button
              onClick={handleNext}
              className="flex items-center space-x-2 px-6 py-2 rounded-lg transition-all duration-300 relative overflow-hidden group"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                color: colors.white,
                border: 'none',
                ...fontStyles.medium,
                boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25)';
              }}
            >
              <span>
                {stepInfo.isLast ? 'Complete' : 'Next'}
              </span>
              {stepInfo.isLast ? (
                <Check size={16} />
              ) : (
                <ArrowRight size={16} />
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingModal;